import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import {
    AuthenticatedTemplate,
    UnauthenticatedTemplate,
} from '@azure/msal-react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { setPaginationGridData, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { AuthProvider } from 'common/contexts/AuthContext';
import { ColorModeContext } from 'common/contexts/ColorModeContext';
import { ErrorBoundary } from 'common/hocs/ErrorBoundary';
import { AppRoutes } from 'common/routes';
import { useFeatures } from 'flagged';

import { useAppDispatch } from './hooks';
import { Layout } from './layout';

const storageModeKey = 'lastAppliedColorMode';

type modeType = 'light' | 'dark';

function getModeToApply(prefersDarkMode: boolean, themeSwitch: boolean): modeType {

    const storageMode = localStorage.getItem(storageModeKey);

    // the only mode allowed when the ff is off is light theme
    if (!themeSwitch) {
        return 'light';
    }

    // it can be any value in the storage, so we need this filter
    if ((storageMode === 'light' || storageMode === 'dark')) {
        return storageMode;
    }

    // system preference mode
    if (prefersDarkMode) {
        return 'dark';
    }

    // fallback value
    return 'light';
}

export function App() {
    // system preference
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const { themeSwitch } = useFeatures();
    const modeToApply = getModeToApply(prefersDarkMode, themeSwitch as boolean);
    const [mode, setMode] = React.useState<modeType>(modeToApply);

    React.useEffect(() => {
        setMode(modeToApply);
    }, [modeToApply]);

    const colorMode = useMemo(() => ({
        toggleColorMode: () => {
            setMode((prevMode) => {
                const newModeToApply: modeType = prevMode === 'light' ? 'dark' : 'light';
                localStorage.setItem(storageModeKey, newModeToApply);
                return newModeToApply;
            });
        },
        currentMode: mode,
    }), [mode]);

    const location = useLocation();
    const dispatch = useAppDispatch();

    React.useEffect(() => {
        dispatch(setRows([]));
        dispatch(setPaginationGridData({}));
    }, [location.pathname]);

    return (
        <ErrorBoundary>
            <AuthProvider>
                <AuthenticatedTemplate>
                    <ColorModeContext.Provider value={colorMode}>
                        <Layout mode={mode}>
                            <AppRoutes />
                        </Layout>
                    </ColorModeContext.Provider>
                </AuthenticatedTemplate>
                <UnauthenticatedTemplate>
                    <p data-testid="app-ready">You are not signed in! Please sign in.</p>
                </UnauthenticatedTemplate>
            </AuthProvider>
        </ErrorBoundary>
    );
}
